import type { ReactNode } from 'react';
import { Modal } from '@/components/common/AppModal';
import { Spinner } from 'react-bootstrap';
import {
  FiCalendar,
  FiClock,
  FiPackage,
} from 'react-icons/fi';
import { TbSparkles as Sparkles, TbWallet as Wallet } from 'react-icons/tb';
import { TbCrown as Crown, TbRadar as Radar } from 'react-icons/tb';
import FalconCloseButton from '@/components/common/FalconCloseButton';
import IconAlert from '@/components/common/IconAlert';
import { OrderStatusRenderer } from '@/app/common/components/CellRenderers/OrderStatusRenderer';
import { formatToman } from '@/app/Pricing/utils';
import { ui as pricingUi, numberFormat } from '@/app/Pricing/constants';
import '@/app/Pricing/styles.css';
import timeStampToDate from '@/helpers/timeStampToDate';
import { toPersianDigit } from '@/helpers/utils';
import type { OrderAddon, OrderById } from '../types/ordersApiTypes';
import {
  getAddonDetailRows,
  getAddonLabel,
} from '../utils/orderAddonDisplay';
import '../styles/order-details.css';

type OrderDetailsModalProps = {
  show: boolean;
  onHide: () => void;
  order?: OrderById;
  isLoading: boolean;
  isError: boolean;
  errorMessage?: string | null;
};

function formatTimestamp(value?: number): string {
  if (!value) return '—';
  return toPersianDigit(timeStampToDate(Number(value)).slice(8));
}

function formatMoney(amount: number): string {
  if (amount === 0) return pricingUi.common.free;
  return formatToman(amount);
}

function formatDeduction(amount: number): string {
  return `${numberFormat.format(amount)}− ${pricingUi.common.toman}`;
}

function PriceLine({
  label,
  value,
  discount = false,
}: {
  label: string;
  value: string;
  discount?: boolean;
}) {
  return (
    <div className={discount ? 'discount-line' : undefined}>
      <span>{label}</span>
      <strong className="text-900">{value}</strong>
    </div>
  );
}

function MetaItem({
  icon,
  label,
  value,
}: {
  icon: ReactNode;
  label: string;
  value: ReactNode;
}) {
  return (
    <li>
      {icon}
      <span>
        {label}: <strong>{value}</strong>
      </span>
    </li>
  );
}

function getAddonCardClass(name: string): string {
  switch (name) {
    case 'wallet':
      return 'wallet-topup-card enabled order-addon-card';
    case 'tracker':
    case 'trcaker':
      return 'tracker-card enabled order-addon-card';
    case 'mentoring_seo':
    case 'mentoring_seo_360':
      return 'mentoring-card enabled order-addon-card';
    default:
      return 'tracker-card enabled order-addon-card';
  }
}

function AddonIcon({ name }: { name: string }) {
  const size = 24;

  switch (name) {
    case 'wallet':
      return <Wallet size={size} />;
    case 'tracker':
    case 'trcaker':
      return <Radar size={size} />;
    case 'mentoring_seo':
    case 'mentoring_seo_360':
      return <Crown size={size} />;
    default:
      return <FiPackage size={20} />;
  }
}

function getAddonIconClass(name: string): string {
  switch (name) {
    case 'wallet':
      return 'tracker-icon wallet-topup-icon rounded-3';
    case 'mentoring_seo':
    case 'mentoring_seo_360':
      return 'tracker-icon mentoring-icon rounded-3';
    default:
      return 'tracker-icon rounded-3';
  }
}

function getAddonEyebrowClass(name: string): string {
  switch (name) {
    case 'wallet':
      return 'eyebrow wallet-topup-eyebrow fs--2';
    case 'mentoring_seo':
    case 'mentoring_seo_360':
      return 'eyebrow mentoring-eyebrow fs--2';
    default:
      return 'eyebrow fs--2 text-secondary';
  }
}

function AddonCard({ addon }: { addon: OrderAddon }) {
  const details = getAddonDetailRows(addon);
  const cardClass = getAddonCardClass(addon.name);

  return (
    <section className={`${cardClass} rounded-4`}>
      <div className="tracker-top">
        <div className="tracker-title">
          <span className={getAddonIconClass(addon.name)}>
            <AddonIcon name={addon.name} />
          </span>
          <div>
            <span className={getAddonEyebrowClass(addon.name)}>افزونه</span>
            <h2 className="text-1000">{getAddonLabel(addon.name)}</h2>
          </div>
        </div>
        <span className="order-addon-card__amount">{formatMoney(addon.amount_paid)}</span>
      </div>

      {details.length > 0 ? (
        <div className="order-addon-details">
          {details.map((row) => (
            <div key={row.label}>
              <span>{row.label}</span>
              <strong>{row.value}</strong>
            </div>
          ))}
        </div>
      ) : null}
    </section>
  );
}

function getPanelRemainingLabel(order: OrderById): string | null {
  if (
    order.remaining_amount_from_panel != null &&
    order.remaining_amount_from_panel > 0
  ) {
    return formatMoney(order.remaining_amount_from_panel);
  }

  if (order.remaining_days_from_panel > 0) {
    return `${order.remaining_days_from_panel.toLocaleString('fa-IR')} روز`;
  }

  return null;
}

function OrderDetailsModal({
  show,
  onHide,
  order,
  isLoading,
  isError,
  errorMessage,
}: OrderDetailsModalProps) {
  const hasDiscount =
    (order?.tariff_discount_amount ?? 0) > 0 || (order?.discount_code_amount ?? 0) > 0;
  const panelRemaining = order ? getPanelRemainingLabel(order) : null;
  const hasWalletDeduction = (order?.wallet_deduction_amount ?? 0) > 0;

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="lg"
      centered
      scrollable
      className="order-details-pricing-modal pb-7 pb-md-0"
    >
      <div className="new-pricing order-details-pricing text-900">
        <div className="order-details-shell">
          <div className="order-details-close">
            <FalconCloseButton onClick={onHide} />
          </div>

          {isLoading ? (
            <div className="order-details-loading d-flex flex-column align-items-center justify-content-center gap-3 p-5">
              <Spinner animation="border" variant="primary" />
              <span className="text-secondary fs--1">در حال دریافت جزئیات سفارش...</span>
            </div>
          ) : null}

          {!isLoading && isError ? (
            <div className="p-4">
              <IconAlert variant="danger">
                {errorMessage || 'دریافت جزئیات سفارش ناموفق بود. لطفاً دوباره تلاش کنید.'}
              </IconAlert>
            </div>
          ) : null}

          {!isLoading && !isError && order ? (
            <aside className="summary-card rounded-0 bg-white dark__bg-card-dark">
              <div className="order-details-topbar">
                <div className="summary-badge fs--2 rounded-2 mb-0">
                  <Sparkles size={16} />
                  جزئیات سفارش
                </div>
              </div>

              <div className="order-details-hero rounded-3">
                <div className="order-details-hero__main">
                  <span className="order-details-hero__icon rounded-3">
                    <FiPackage size={24} />
                  </span>
                  <div className="order-details-plan-copy">
                    <strong className="text-1000">{order.tariff_name || '—'}</strong>
                    <span className="text-secondary fs--2">
                      {order.period || '—'}
                      {order.number ? ` · شماره ${order.number}` : ''}
                    </span>
                  </div>
                </div>
                <div className="order-details-hero__status">
                  <OrderStatusRenderer status={order.status} />
                </div>
              </div>

              <ul className="order-details-meta">
                <MetaItem
                  icon={<FiCalendar size={15} />}
                  label="تاریخ سفارش"
                  value={formatTimestamp(order.ordered_at)}
                />
                <MetaItem
                  icon={<FiClock size={15} />}
                  label="تاریخ انقضا"
                  value={formatTimestamp(order.expired_at)}
                />
              </ul>

              <div className="price-details order-details-price-lines text-secondary fs--1">
                <PriceLine label="مبلغ طرح" value={formatMoney(order.tariff_amount)} />
                {panelRemaining ? (
                  <PriceLine
                    label="مانده از طرح قبلی"
                    value={
                      order.remaining_amount_from_panel != null &&
                      order.remaining_amount_from_panel > 0
                        ? formatDeduction(order.remaining_amount_from_panel)
                        : panelRemaining
                    }
                    discount={
                      order.remaining_amount_from_panel != null &&
                      order.remaining_amount_from_panel > 0
                    }
                  />
                ) : null}
                {order.addons?.map((addon) => (
                  <PriceLine
                    key={addon.addon_id}
                    label={getAddonLabel(addon.name)}
                    value={formatMoney(addon.amount_paid)}
                  />
                ))}
                {order.tariff_discount_amount ? (
                  <PriceLine
                    label="تخفیف طرح"
                    value={formatDeduction(order.tariff_discount_amount)}
                    discount
                  />
                ) : null}
                {order.discount_code_amount ? (
                  <PriceLine
                    label="تخفیف کد معرف"
                    value={formatDeduction(order.discount_code_amount)}
                    discount
                  />
                ) : null}
                <PriceLine label="مالیات بر ارزش افزوده" value={formatMoney(order.tax_amount)} />
                {hasWalletDeduction ? (
                  <PriceLine
                    label="کسر از کیف پول"
                    value={formatDeduction(order.wallet_deduction_amount)}
                    discount
                  />
                ) : null}
              </div>

              <div className="total-row">
                <div>
                  <span className="text-secondary">مبلغ پرداختی</span>
                  <small>
                    {hasDiscount ? 'شامل تخفیف‌ها' : order.period || 'سفارش'}
                  </small>
                </div>
                <strong>{formatMoney(order.amount_paid)}</strong>
              </div>

              <div className="order-details-section-title">
                <Sparkles size={16} />
                افزونه‌های سفارش
              </div>

              {order.addons?.length ? (
                <div className="order-details-addons">
                  {order.addons.map((addon) => (
                    <AddonCard key={addon.addon_id} addon={addon} />
                  ))}
                </div>
              ) : (
                <div className="order-details-empty">
                  <FiPackage size={28} className="opacity-50" />
                  <span className="fs--1">این سفارش افزونه‌ای ندارد.</span>
                </div>
              )}
            </aside>
          ) : null}
        </div>
      </div>
    </Modal>
  );
}

export default OrderDetailsModal;
